// @ts-strict-ignore
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';

import { LandmarkPanel } from '@form/lm-form.model';
import { LMFormatterService } from '@list/formatters/lm-formatter.service';
import { LMListCardService } from '@list/services/lm-list-card.service';
import { LMModel } from '@form/model/lm-model';
import { LMWorker } from 'app/types/lm-worker';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { LMPanelTransactionEvent } from '@form/services/lm-form-panel.service';
import { LMRefreshRequest } from '@app/components';
import { LMTwelveColumnPanelBaseComponent } from './lm-twelve-column-panel-base.component';
import { LMTwelveColumnPanelListComponent } from './lm-twelve-column-panel-list.component';
import { LMConditionUtilService } from '@app/utils';

import { isEqual } from 'lodash-es';

export class LMTwelveColumnGridPanelsSaveWorker extends LMWorker {
  private panels: LMTwelveColumnPanelBaseComponent[];

  constructor(panels: LMTwelveColumnPanelBaseComponent[]) {
    super();

    this.panels = panels;
  }

  protected has(i: number): boolean {
    return (i < this.panels.length);
  }

  protected get(i: number): LMTwelveColumnPanelBaseComponent {
    return this.panels[ i ];
  }

  protected run(i: number): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const panel = this.panels[ i ];
      if (panel && panel.isModified()) {
        panel.save(observer);
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }
}

@Component({
  selector: 'lm-twelve-column-grid',
  templateUrl: './lm-twelve-column-grid.component.html',
  styleUrls: [ './lm-twelve-column-grid.component.scss' ],
  providers: [ LMFormatterService, LMListCardService ]
})
export class LMTwelveColumnGridComponent implements OnInit, OnDestroy {

  /**
   * The formUrlInfo of the main form that uses this twelve column grid.
   * This will not be used for lists with dashboard/detail sections.
   * Can likely be removed once the new spec-url refactor is complete
   * and solely in use.
   */
  @Input() formUrlInfo: LMFormUrlInfo;

  @Input() set dashboardPanels(panels: Array<LandmarkPanel>) { // used by list and form for detail sections and form tabs.
    this.m_cards = panels;
  }

  get dashboardPanels(): Array<LandmarkPanel> {
    return this.m_cards.filter(panel => panel.isVisible && !panel.hidden && panel.isValid);
  }

  @Input() model: LMModel; // used by composite forms when displaying panels as dashboard panes
  @Input() specFields: LMSpecFields; // used by composite forms when displaying panels as dashboard panes
  @Input() placeHolder: ViewContainerRef; // used by composite forms when displaying panels as dashboard panes
  @Input() set fromDataView(fromDataView: LMDataView) { // optional if used in a page panel
    this.m_fromDataView = fromDataView;
    this.calculateRows();
  }

  get fromDataView(): LMDataView {
    return this.m_fromDataView;
  }

  @Input() set messageRuleView(messageRuleView: LMDataView) {
    this.m_messageRuleView = messageRuleView;
  }

  get messageRuleView(): LMDataView {
    return this.m_messageRuleView;
  }

  @Input() set panelVisibilityUpdateTimeStamp(timeStamp: number) {
    if (timeStamp !== this.m_panelVisibilityUpdateTimeStamp) {
      this.m_panelVisibilityUpdateTimeStamp = timeStamp;
      this.calculateRows();
    }
  }

  @Input() displayedOnPagePanel: boolean;

  @Output() contentModified = new EventEmitter<any>();
  @Output() uiLinkEvent = new EventEmitter<any>();
  @Output() fieldEvent = new EventEmitter<LMFieldEvent>();
  @Output() dashboardContentDisplayed = new EventEmitter<any>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() panelTransactionEvent = new EventEmitter<LMPanelTransactionEvent>();
  @Output() drillAroundEvent = new EventEmitter<LMDrillAroundEvent>();

  @ViewChildren(LMTwelveColumnPanelBaseComponent) lmCardComponentList: QueryList<LMTwelveColumnPanelBaseComponent>;
  @ViewChildren('card') lmCards: QueryList<LMTwelveColumnPanelBaseComponent>;

  @HostBinding('class.full-width') get isFullWidth() { return true; }
  @HostBinding('class.homepage-background') get isHomepageBackground() { return true; }

  public rows: Array<Array<LandmarkPanel>> = [];
  public _rows: Array<Array<LandmarkPanel>> = [];

  private m_messageRuleView: LMDataView;
  private m_fromDataView: LMDataView;
  private m_cards: Array<LandmarkPanel>;
  private m_panelVisibilityUpdateTimeStamp: number;
  private displayedCards = 0;
  private modelStatusChangesSubscription: Subscription;

  constructor(private elementRef: ElementRef, private lmConditionUtilService: LMConditionUtilService) {}

  ngOnInit() {
    if (this.dashboardPanels && this.model) {
      // In composite form, since visible/valid when rules can be changed from other values on the form, need to
      // subscribe to values changing and recalculate the rows
      this.modelStatusChangesSubscription = this.lmConditionUtilService.evaluateConditions.subscribe(() => {
        this.calculateRows();
      });
    }
  }

  ngOnDestroy() {
    if (this.modelStatusChangesSubscription) {
      this.modelStatusChangesSubscription.unsubscribe();
    }
  }

  isModified(): boolean {
    return this.lmCards.some(dashboardPanel => dashboardPanel.isModified());
  }

  private includeInRefresh(cardComponent: LMTwelveColumnPanelBaseComponent, request?: LMRefreshRequest): boolean {
    if (cardComponent instanceof LMTwelveColumnPanelListComponent) {
      const dashBoardList = <LMTwelveColumnPanelListComponent>cardComponent;
      return dashBoardList.lmListComponent.listId !== request?.originatingSourceId;
    } else {
      return true;
    }
  }

  refresh(request?: LMRefreshRequest): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const refreshCalls: Observable<boolean>[] = this.lmCards.toArray()
        .filter(dashboardComponent => this.includeInRefresh(dashboardComponent, request))
        .map((dashboardComponent: LMTwelveColumnPanelBaseComponent) => dashboardComponent.refresh());

      forkJoin(refreshCalls).subscribe(() => {
        observer.next(true);
        observer.complete();
      });
    });
  }

  runSaveAction(): Observable<boolean> {
    const dashboardPanels: LMTwelveColumnPanelBaseComponent[] = this.lmCards.toArray();

    return new Observable<boolean>(observer => {
      this.runSavePanels(dashboardPanels).subscribe(
        () => {
          observer.next(true);
          observer.complete();
        },
        (() => {
          observer.next(false);
          observer.complete();
        }));
    });
  }

  saveDashboardViewState() {
    this.lmCardComponentList.forEach(dashboardComponent => dashboardComponent.savePanelState());
  }

  clearDashboardViewState() {
    this.lmCardComponentList.forEach(dashboardComponent => dashboardComponent.clearPanelState());
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onUILinkEvent(event) {
    this.uiLinkEvent.emit(event);
  }

  onFieldEvent(event: LMFieldEvent) {
    this.fieldEvent.emit(event);
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onPanelTransactionEvent(event: LMPanelTransactionEvent) {
    this.panelTransactionEvent.emit(event);
  }

  onDrillAroundEvent(event: LMDrillAroundEvent) {
    this.drillAroundEvent.emit(event);
  }

  onComponentDisplayed() {
    this.displayedCards = this.displayedCards + 1;
    if (this.displayedCards === this.dashboardPanels.length) {
      this.dashboardContentDisplayed.emit();
    }
  }

  runSavePanels(dashboardPanels: LMTwelveColumnPanelBaseComponent[]): Observable<boolean> {
    const saveWorker: LMTwelveColumnGridPanelsSaveWorker = new LMTwelveColumnGridPanelsSaveWorker(dashboardPanels);
    return saveWorker.start();
  }

  public getPanelHeight(): number {
    if (this.elementRef) {
      return this.elementRef.nativeElement.offsetHeight;
    }
    return 0;
  }

  // Need to split into rows of at most 12 spanning column
  private calculateRows(): void {
    let width = 0;
    let rowPanels: Array<LandmarkPanel> = [];
    this._rows = [];
    this.dashboardPanels.forEach((panel: LandmarkPanel, index: number) => {
      // if the panel spans 12 (takes full width), push the already created row and then the new panel onto the
      // rows array
      if (panel.spanningColumns === 12) {
        if (rowPanels.length > 0) {
          this._rows.push(rowPanels);
        }
        this._rows.push([ panel ]);
        width = 0;
        rowPanels = [];
      } else {
        // check to see what the total spanning width would be. Push the row if the width would be greater than 12.
        // if not, push the panel on to the row
        const tempWidth = width + panel.spanningColumns;
        if (tempWidth < 13) {
          width += panel.spanningColumns;
          rowPanels.push(panel);
        } else {
          this._rows.push(rowPanels);
          width = panel.spanningColumns;
          rowPanels = [];
          rowPanels.push(panel);
        }

        // when on the last panel, push the row in the rows object
        if (index === this.dashboardPanels.length - 1) {
          this._rows.push(rowPanels);
        }
      }
    });

    if (!isEqual(this._rows, this.rows)) {
      this.rows = this._rows
    }
  }
}
